// STEP REVIEWER-DEMO-ACCOUNT-1 · External reviewer / demo ID+PW login page.
// Not linked from the normal Login page. Direct-URL only: /reviewer-login.
// Regular consumers keep Kakao / Google. No signup form here.
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LivemoaLegalHeader from '@/components/LivemoaLegalHeader';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReviewerLogin({ setUser }) {
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const returnUrl = params.get('return_url') || '/seller/dashboard';

  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/reviewer/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_id: loginId.trim().toLowerCase(), password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.detail === 'invalid_credentials'
          ? 'Invalid ID or password.'
          : (body?.detail || `Login failed (${res.status}).`));
        setBusy(false);
        return;
      }
      const user = await res.json();
      if (user?.session_token) {
        try { localStorage.setItem('session_token', user.session_token); } catch (_e) {}
      }
      if (typeof setUser === 'function') setUser(user);
      nav(returnUrl, { replace: true });
    } catch (err) {
      setError(err?.message || 'Network error.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="reviewer-login-page">
      <LivemoaLegalHeader current="reviewer" />
      <main className="flex items-center justify-center px-4 py-14">
        <form onSubmit={submit} className="w-full max-w-sm border border-black/10 dark:border-white/10 rounded-lg p-8 bg-white dark:bg-[#141414]">
          <h1 className="text-2xl font-bold mb-2" data-testid="reviewer-login-title">
            Reviewer Login
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
            External reviewers (Meta App Review, partner demos) sign in here with
            the ID and password provided by LIVEMOA. Regular users please use the
            <a href="/login" className="underline mx-1">standard login</a>page.
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-xs text-red-700 dark:text-red-300"
                 data-testid="reviewer-login-error">
              {error}
            </div>
          )}

          <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">Login ID</label>
          <input
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            className="w-full mb-4 px-3 py-2 rounded-md border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f] text-sm"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            data-testid="reviewer-login-id"
            required
          />

          <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">Password</label>
          <input
            type="password"
            autoComplete="current-password"
            className="w-full mb-6 px-3 py-2 rounded-md border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f] text-sm"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reviewer-login-password"
            required
          />

          <button
            type="submit"
            disabled={busy || !loginId || !password}
            className="w-full bg-black text-white dark:bg-white dark:text-black font-bold py-3 rounded-md disabled:opacity-50"
            data-testid="reviewer-login-submit"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="mt-6 text-[10px] text-gray-500 dark:text-gray-400 text-center">
            By signing in you agree to the LIVEMOA Terms and Privacy Policy.
          </p>
        </form>
      </main>
    </div>
  );
}
